import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useNotifications } from "../../context/NotificationsContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { useIndustryLabels } from "../../context/IndustryContext";
import Main from "./Main";
import SeidBar from "./SeidBar";
import MobileBottomNav from "./MobileBottomNav";
import NotificationDetailModal from "./NotificationDetailModal";
import AssistantChat from "./AssistantChat";
import AppLoadingScreen from "./AppLoadingScreen";
import WorkforceLeadershipBanner from "./WorkforceLeadershipBanner";
import {
  buildMobileBottomNavItems,
  getPageTitle,
} from "../../utils/mobileBottomNav";
import "./EssHome.css";
import "./MobileBottomNav.css";
import { countComplaintsNeedingViewerAction } from "../../utils/complaintHelpers";
import {
  notificationFromPushData,
  resolveNotificationPageId,
  resolveNotificationTarget,
  hasNotificationTarget,
  countUnreadAnnouncements,
} from "../../utils/notificationTarget";
import {
  canSeeWfhNav,
  canUseRemoteAttendance,
} from "../../utils/workModeHelpers";
import { useMyWorkMode } from "../../hooks/useMyWorkMode";
import { useProfileCompletionLock } from "../../hooks/useProfileCompletionLock";
import { NavigationProvider } from "../../context/NavigationContext";
import { buildActionBadges, countAwaitingUserActions } from "../../utils/pendingActionBadges";
import { isNavItemVisible } from "../../utils/featureVisibility";
import { profileCompletionBannerText } from "../../utils/profileCompletionLock";
import {
  consumePendingPushAction,
  hasPendingPushAction,
  replayPendingPushAction,
} from "../../services/pushNotifications";
import { consumeHardwareBackForFocusedField } from "../../utils/mobile";

const DEFAULT_PAGE_BY_ROLE = {
  employee: "home",
  manager: "home",
  "department-manager": "dashboard",
  hr: "dashboard",
  "hr-staff": "dashboard",
  gm: "dashboard",
  owner: "dashboard",
  admin: "accounts",
  "platform-admin": "company-modules",
};

function EssHome() {
  const { user, logout } = useAuth();
  const {
    appeals,
    complaints,
    resignations,
    employeesByBranch,
    pendingRequests,
    teamMembers,
    employeePenalties,
    wfhRequests,
    recruitmentInbox,
    pendingEvaluationCount,
    hasLoaded,
  } = useData();
  const { markAsRead, refreshNotifications, unreadCount, notifications } =
    useNotifications();
  const [activeId, setActiveId] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pushNotification, setPushNotification] = useState(null);
  const [focusAnnouncementId, setFocusAnnouncementId] = useState(null);
  const [focusPolicyId, setFocusPolicyId] = useState(null);
  const [foregroundBanner, setForegroundBanner] = useState(null);
  const foregroundBannerTimerRef = useRef(null);

  const { workMode, resolved: workModeResolved } = useMyWorkMode();
  const profileLock = useProfileCompletionLock();
  const isProfileLocked = Boolean(profileLock.isLocked);
  const featureFlags = useFeatureFlags();
  const industryLabels = useIndustryLabels();

  const bottomNavItems = useMemo(
    () =>
      buildMobileBottomNavItems({
        userRole: user?.role,
        workMode,
        featureFlags,
      }),
    [user?.role, workMode, featureFlags],
  );

  const pageTitle = getPageTitle(activeId, "الخدمة الذاتية", industryLabels);

  const handleNavSelect = useCallback((id, options = {}) => {
    if (isProfileLocked) return;
    setActiveId(id);
    setFocusAnnouncementId(options.announcementId || null);
    setFocusPolicyId(options.policyId || null);
    setSidebarOpen(false);
  }, [isProfileLocked]);

  const appliedRoleHomeRef = useRef(false);

  useEffect(() => {
    if (!user?.role || hasPendingPushAction()) return;

    if (isProfileLocked) {
      setActiveId("my-profile");
      appliedRoleHomeRef.current = true;
      return;
    }

    // Land on the role home once. Do not send the user back after they
    // opened a request form — isProfileLocked/role can settle later.
    if (appliedRoleHomeRef.current) return;
    appliedRoleHomeRef.current = true;
    setActiveId(DEFAULT_PAGE_BY_ROLE[user.role] || "home");
  }, [user?.role, isProfileLocked]);

  // منع فتح حضور التطبيق / العمل عن بُعد لمن لا يحق له (إشعار أو حالة قديمة)
  // لا نُرجع للرئيسية قبل تأكيد workMode من /employees/me حتى لا يُطرد
  // موظف حُوِّل لـ HYBRID/REMOTE بينما الجلسة ما زالت OFFICE.
  useEffect(() => {
    if (isProfileLocked || !workModeResolved) return;
    const backendRoles = user?.backendRoles || [];
    if (
      activeId === "attendance" &&
      !canUseRemoteAttendance(workMode)
    ) {
      setActiveId(DEFAULT_PAGE_BY_ROLE[user?.role] || "home");
      return;
    }
    if (
      activeId === "wfh" &&
      !canSeeWfhNav(workMode, user?.role, backendRoles)
    ) {
      setActiveId(DEFAULT_PAGE_BY_ROLE[user?.role] || "home");
    }
  }, [
    activeId,
    workMode,
    workModeResolved,
    user?.role,
    user?.backendRoles,
    isProfileLocked,
  ]);

  // Android back: close overlays / return to default page — never logout
  useEffect(() => {
    function handleAppBack(event) {
      if (consumeHardwareBackForFocusedField()) {
        event.preventDefault();
        return;
      }
      if (pushNotification) {
        event.preventDefault();
        setPushNotification(null);
        return;
      }
      if (foregroundBanner) {
        event.preventDefault();
        setForegroundBanner(null);
        return;
      }
      if (sidebarOpen) {
        event.preventDefault();
        setSidebarOpen(false);
        return;
      }
      const defaultPage = isProfileLocked
        ? "my-profile"
        : DEFAULT_PAGE_BY_ROLE[user?.role] || "home";
      if (activeId !== defaultPage) {
        event.preventDefault();
        setActiveId(defaultPage);
      }
      // On default page: leave prevented so mobile.js does not leave the session
    }

    window.addEventListener("app:backbutton", handleAppBack);
    return () => window.removeEventListener("app:backbutton", handleAppBack);
  }, [
    pushNotification,
    foregroundBanner,
    sidebarOpen,
    activeId,
    user?.role,
    isProfileLocked,
  ]);

  // Open the correct page (and detail modal) when user taps a push notification
  useEffect(() => {
    function handlePushAction(event) {
      if (isProfileLocked) return;

      const action = event.detail;
      const data =
        action?.notification?.data ||
        action?.notification?.extra ||
        action?.data ||
        {};

      const notif = notificationFromPushData(data);
      if (!notif) return;

      // Prefer system notification title/body when data payload omits them
      if (!notif.title && action?.notification?.title) {
        notif.title = action.notification.title;
      }
      if (!notif.message && action?.notification?.body) {
        notif.message = action.notification.body;
      }

      const target = resolveNotificationTarget(notif);
      const pageId = resolveNotificationPageId(target, user?.role);
      setActiveId(pageId);
      setSidebarOpen(false);
      setForegroundBanner(null);

      if (notif.id) {
        markAsRead(notif.id).catch(() => {});
      }

      if (target?.kind === "investigation" && target.id) {
        sessionStorage.setItem("hrms-investigation-focus", String(target.id));
        window.dispatchEvent(new Event("investigation:focus"));
        setFocusAnnouncementId(null);
        setFocusPolicyId(null);
        setPushNotification(null);
      } else if (target?.kind === "announcement" && target.id) {
        setFocusAnnouncementId(String(target.id));
        setFocusPolicyId(null);
        setPushNotification(null);
      } else if (target?.kind === "company_policy" && target.id) {
        setFocusPolicyId(String(target.id));
        setFocusAnnouncementId(null);
        setPushNotification(null);
      } else if (hasNotificationTarget(notif)) {
        setFocusAnnouncementId(null);
        setFocusPolicyId(null);
        setPushNotification(notif);
      }

      consumePendingPushAction();
    }

    window.addEventListener("push:action", handlePushAction);

    // Terminated cold start: Capacitor may fire before this listener exists.
    replayPendingPushAction();

    return () => window.removeEventListener("push:action", handlePushAction);
  }, [isProfileLocked, user?.role, markAsRead]);

  // Foreground: show in-app banner when a push arrives while the app is open
  useEffect(() => {
    function handlePushReceived(event) {
      if (isProfileLocked) return;

      const notification = event.detail || {};
      const data = notification.data || notification.extra || {};
      const title =
        notification.title || data.title || "إشعار جديد";
      const body =
        notification.body || data.body || data.message || "";

      setForegroundBanner({ title, body, data });
      refreshNotifications?.().catch(() => {});

      if (foregroundBannerTimerRef.current) {
        window.clearTimeout(foregroundBannerTimerRef.current);
      }
      foregroundBannerTimerRef.current = window.setTimeout(() => {
        setForegroundBanner(null);
      }, 6000);
    }

    window.addEventListener("push:received", handlePushReceived);
    return () => {
      window.removeEventListener("push:received", handlePushReceived);
      if (foregroundBannerTimerRef.current) {
        window.clearTimeout(foregroundBannerTimerRef.current);
      }
    };
  }, [isProfileLocked, refreshNotifications]);

  function handleForegroundBannerTap() {
    if (!foregroundBanner || isProfileLocked) return;
    const notif = notificationFromPushData(foregroundBanner.data || {});
    if (notif) {
      if (!notif.title) notif.title = foregroundBanner.title;
      if (!notif.message) notif.message = foregroundBanner.body;
      const target = resolveNotificationTarget(notif);
      setActiveId(resolveNotificationPageId(target, user?.role));
      if (target?.kind === "investigation" && target.id) {
        sessionStorage.setItem("hrms-investigation-focus", String(target.id));
        window.dispatchEvent(new Event("investigation:focus"));
        setFocusAnnouncementId(null);
        setFocusPolicyId(null);
        setPushNotification(null);
      } else if (target?.kind === "announcement" && target.id) {
        setFocusAnnouncementId(String(target.id));
        setFocusPolicyId(null);
        setPushNotification(null);
      } else if (target?.kind === "company_policy" && target.id) {
        setFocusPolicyId(String(target.id));
        setFocusAnnouncementId(null);
        setPushNotification(null);
      } else if (hasNotificationTarget(notif)) {
        setFocusAnnouncementId(null);
        setFocusPolicyId(null);
        setPushNotification(notif);
      } else {
        setActiveId("notifications");
      }
    } else {
      setActiveId("notifications");
    }
    setForegroundBanner(null);
  }

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!profileLock.resolved || (!isProfileLocked && !hasLoaded)) {
    return <AppLoadingScreen message="جارٍ تحميل البيانات..." />;
  }

  const receivedComplaintsCount = countComplaintsNeedingViewerAction(
    complaints,
    user,
  );

  const badges = buildActionBadges({
    user,
    pendingRequests,
    teamMembers,
    appeals,
    employeesByBranch,
    unreadCount,
    unreadAnnouncementCount: countUnreadAnnouncements(notifications),
    receivedComplaintsCount,
    resignations,
    employeePenalties,
    wfhRequests,
    recruitmentInbox,
    pendingEvaluationCount: isNavItemVisible(featureFlags, "evaluations")
      ? pendingEvaluationCount
      : 0,
  });
  const awaitingCount = isProfileLocked
    ? 0
    : countAwaitingUserActions(badges, user?.role);

  const renderMobileHeader = (title, { showMenuButton = false } = {}) => (
    <header
      className={`ess-mobile-header ${
        showMenuButton ? "" : "ess-mobile-header--compact"
      }`}
    >
      {showMenuButton ? (
        <button
          type="button"
          className="ess-mobile-menu-btn"
          onClick={() => setSidebarOpen(true)}
          aria-label={
            awaitingCount > 0
              ? `فتح القائمة، ${awaitingCount} بانتظارك`
              : "فتح القائمة"
          }
        >
          <Menu size={22} />
          {awaitingCount > 0 && (
            <span className="ess-mobile-menu-badge">
              {awaitingCount > 99 ? "99+" : awaitingCount}
            </span>
          )}
        </button>
      ) : null}
      <span className="ess-mobile-header-title">{title}</span>
      {showMenuButton ? (
        <span className="ess-mobile-header-user">{user?.name}</span>
      ) : null}
    </header>
  );

  if (isProfileLocked) {
    return (
      <NavigationProvider activeId="my-profile" onNavigate={handleNavSelect}>
      <div className="ess-layout">
        <div
          className={`ess-sidebar-overlay ${sidebarOpen ? "is-visible" : ""}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
        <SeidBar
          activeId="my-profile"
          onSelect={handleNavSelect}
          userName={user?.name}
          userRole={user?.role}
          userDepartmentId={user?.departmentId}
          userJobTitle={user?.jobTitle}
          onLogout={logout}
          badges={{}}
          mobileOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          profileLocked
        />
        <div className="ess-main-wrapper">
          {renderMobileHeader("ملفي الشخصي", { showMenuButton: true })}
          <WorkforceLeadershipBanner />
          <div className="ess-content">
            <Main
              className=""
              activeId="my-profile"
              userRole={user?.role}
              userName={user?.name}
              focusAnnouncementId={null}
            />
          </div>
        </div>
      </div>
      </NavigationProvider>
    );
  }

  const graceBanner = profileCompletionBannerText(profileLock);

  return (
    <NavigationProvider activeId={activeId} onNavigate={handleNavSelect}>
    <div className="ess-layout ess-has-bottom-nav">
      {foregroundBanner && (
        <button
          type="button"
          className="ess-push-banner"
          onClick={handleForegroundBannerTap}
        >
          <span className="ess-push-banner-title">{foregroundBanner.title}</span>
          {foregroundBanner.body ? (
            <span className="ess-push-banner-body">{foregroundBanner.body}</span>
          ) : null}
        </button>
      )}
      <div
        className={`ess-sidebar-overlay ${sidebarOpen ? "is-visible" : ""}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <SeidBar
        activeId={activeId}
        onSelect={handleNavSelect}
        userName={user?.name}
        userRole={user?.role}
        userDepartmentId={user?.departmentId}
        userJobTitle={user?.jobTitle}
        onLogout={logout}
        badges={badges}
        mobileOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="ess-main-wrapper">
        {renderMobileHeader(pageTitle)}
        <WorkforceLeadershipBanner />
        {graceBanner && activeId !== "my-profile" ? (
          <button
            type="button"
            className="ess-profile-lock-banner"
            onClick={() => handleNavSelect("my-profile")}
          >
            {graceBanner}
          </button>
        ) : null}
        <Main
          className="ess-content"
          activeId={activeId}
          userRole={user?.role}
          userName={user?.name}
          focusAnnouncementId={focusAnnouncementId}
          focusPolicyId={focusPolicyId}
        />
      </div>
      <MobileBottomNav
        items={bottomNavItems}
        activeId={activeId}
        badges={badges}
        sidebarOpen={sidebarOpen}
        onSelect={handleNavSelect}
        onOpenMenu={() => setSidebarOpen((open) => !open)}
      />
      {pushNotification && (
        <NotificationDetailModal
          notification={pushNotification}
          onClose={() => setPushNotification(null)}
        />
      )}
      <AssistantChat />
    </div>
    </NavigationProvider>
  );
}

export default EssHome;
